/**
 * @engine/physics2d - Raycasting System
 *
 * CPU-side raycasting against all physics collider primitives.
 * GPU-compatible query structure (RaycastQueryGPU / RaycastResultGPU).
 *
 * Supports:
 * - Single ray → first hit
 * - Single ray → all hits
 * - Batch rays (for future GPU dispatch)
 * - Layer mask filtering
 */

import type { PhysVec2, RaycastHit2D, ColliderState, PhysicsBodyState } from '../types';
import { ColliderType, CapsuleDirection } from '../types';
import type { SpatialHash } from './BroadPhase';

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Cast a ray and return the closest hit.
 */
export function raycast(
  origin: PhysVec2,
  direction: PhysVec2,
  maxDistance: number,
  bodies: PhysicsBodyState[],
  colliders: ColliderState[],
  broadPhase: SpatialHash,
  layerMask: number = 0xFFFF,
): RaycastHit2D | null {
  // Normalize direction
  const len = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
  if (len < 1e-10) return null;
  const dirX = direction.x / len;
  const dirY = direction.y / len;

  // Query broadphase for candidate colliders along ray
  const candidates = broadPhase.queryRay(origin, { x: dirX, y: dirY }, maxDistance);

  let closestHit: RaycastHit2D | null = null;

  for (const colliderIdx of candidates) {
    if (colliderIdx >= colliders.length) continue;
    const collider = colliders[colliderIdx];

    // Layer filter
    if ((collider.layerMask & layerMask) === 0) continue;

    // Skip triggers by default (use raycastAll + filter instead)
    const body = bodies[collider.bodyIndex];
    if (!body) continue;

    const hit = raycastCollider(
      origin, dirX, dirY, maxDistance,
      collider, body,
    );

    if (hit && (!closestHit || hit.distance < closestHit.distance)) {
      closestHit = hit;
    }
  }

  return closestHit;
}

/**
 * Cast a ray and return ALL hits (unsorted).
 */
export function raycastAll(
  origin: PhysVec2,
  direction: PhysVec2,
  maxDistance: number,
  bodies: PhysicsBodyState[],
  colliders: ColliderState[],
  broadPhase: SpatialHash,
  layerMask: number = 0xFFFF,
): RaycastHit2D[] {
  const len = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
  if (len < 1e-10) return [];
  const dirX = direction.x / len;
  const dirY = direction.y / len;

  const candidates = broadPhase.queryRay(origin, { x: dirX, y: dirY }, maxDistance);
  const hits: RaycastHit2D[] = [];

  for (const colliderIdx of candidates) {
    if (colliderIdx >= colliders.length) continue;
    const collider = colliders[colliderIdx];
    if ((collider.layerMask & layerMask) === 0) continue;

    const body = bodies[collider.bodyIndex];
    if (!body) continue;

    const hit = raycastCollider(origin, dirX, dirY, maxDistance, collider, body);
    if (hit) hits.push(hit);
  }

  return hits;
}

/**
 * Point overlap test — find all colliders containing a point.
 */
export function overlapPoint(
  point: PhysVec2,
  bodies: PhysicsBodyState[],
  colliders: ColliderState[],
  broadPhase: SpatialHash,
  layerMask: number = 0xFFFF,
): number[] {
  const candidates = broadPhase.queryPoint(point.x, point.y);
  const result: number[] = [];

  for (const colliderIdx of candidates) {
    if (colliderIdx >= colliders.length) continue;
    const collider = colliders[colliderIdx];
    if ((collider.layerMask & layerMask) === 0) continue;

    const body = bodies[collider.bodyIndex];
    if (!body) continue;

    if (pointInCollider(point, collider, body)) {
      result.push(colliderIdx);
    }
  }

  return result;
}

/**
 * AABB overlap test — find all colliders overlapping a box.
 */
export function overlapBox(
  center: PhysVec2,
  halfExtents: PhysVec2,
  bodies: PhysicsBodyState[],
  colliders: ColliderState[],
  broadPhase: SpatialHash,
  layerMask: number = 0xFFFF,
): number[] {
  const aabb = {
    minX: center.x - halfExtents.x,
    minY: center.y - halfExtents.y,
    maxX: center.x + halfExtents.x,
    maxY: center.y + halfExtents.y,
  };

  const candidates = broadPhase.queryAABB(aabb);
  const result: number[] = [];

  for (const colliderIdx of candidates) {
    if (colliderIdx >= colliders.length) continue;
    const collider = colliders[colliderIdx];
    if ((collider.layerMask & layerMask) === 0) continue;

    // AABB overlap check
    if (collider.aabbMin.x <= aabb.maxX && collider.aabbMax.x >= aabb.minX &&
        collider.aabbMin.y <= aabb.maxY && collider.aabbMax.y >= aabb.minY) {
      result.push(colliderIdx);
    }
  }

  return result;
}

/**
 * Circle overlap test.
 */
export function overlapCircle(
  center: PhysVec2,
  radius: number,
  bodies: PhysicsBodyState[],
  colliders: ColliderState[],
  broadPhase: SpatialHash,
  layerMask: number = 0xFFFF,
): number[] {
  const aabb = {
    minX: center.x - radius,
    minY: center.y - radius,
    maxX: center.x + radius,
    maxY: center.y + radius,
  };

  const candidates = broadPhase.queryAABB(aabb);
  const result: number[] = [];

  for (const colliderIdx of candidates) {
    if (colliderIdx >= colliders.length) continue;
    const collider = colliders[colliderIdx];
    if ((collider.layerMask & layerMask) === 0) continue;

    // Quick AABB test first
    if (collider.aabbMin.x > aabb.maxX || collider.aabbMax.x < aabb.minX ||
        collider.aabbMin.y > aabb.maxY || collider.aabbMax.y < aabb.minY) {
      continue;
    }

    result.push(colliderIdx);
  }

  return result;
}

// ============================================================================
// PER-SHAPE RAYCAST
// ============================================================================

function raycastCollider(
  origin: PhysVec2,
  dirX: number, dirY: number,
  maxDist: number,
  collider: ColliderState,
  body: PhysicsBodyState,
): RaycastHit2D | null {
  // Transform ray into collider local space
  const cos = Math.cos(body.rotation + collider.rotation);
  const sin = Math.sin(body.rotation + collider.rotation);
  const cx = body.position.x + collider.offset.x * cos - collider.offset.y * sin;
  const cy = body.position.y + collider.offset.x * sin + collider.offset.y * cos;

  // Local origin / direction
  const lox = (origin.x - cx) * cos + (origin.y - cy) * sin;
  const loy = -(origin.x - cx) * sin + (origin.y - cy) * cos;
  const ldx = dirX * cos + dirY * sin;
  const ldy = -dirX * sin + dirY * cos;

  let result: { t: number; nx: number; ny: number } | null = null;

  switch (collider.type) {
    case ColliderType.Circle:
      result = rayVsCircle(lox, loy, ldx, ldy, 0, 0, collider.radius);
      break;
    case ColliderType.Box:
      result = rayVsBox(lox, loy, ldx, ldy, collider.halfExtents.x, collider.halfExtents.y);
      break;
    case ColliderType.Capsule:
      result = rayVsCapsule(lox, loy, ldx, ldy, collider.radius, collider.capsuleHeight, collider.capsuleDirection);
      break;
    case ColliderType.Polygon:
      result = rayVsPolygon(lox, loy, ldx, ldy, collider.vertices, collider.vertexCount);
      break;
  }

  if (!result || result.t < 0 || result.t > maxDist) return null;

  // Transform hit back to world space
  const hitX = origin.x + dirX * result.t;
  const hitY = origin.y + dirY * result.t;
  const wnx = result.nx * cos - result.ny * sin;
  const wny = result.nx * sin + result.ny * cos;

  return {
    point: { x: hitX, y: hitY },
    normal: { x: wnx, y: wny },
    distance: result.t,
    fraction: result.t / maxDist,
    bodyIndex: body.bodyIndex,
    colliderIndex: collider.colliderIndex,
    gameObjectGuid: body.guid,
  };
}

// ============================================================================
// RAY vs PRIMITIVES (local space)
// ============================================================================

function rayVsCircle(
  ox: number, oy: number, dx: number, dy: number,
  cx: number, cy: number, r: number,
): { t: number; nx: number; ny: number } | null {
  const fx = ox - cx;
  const fy = oy - cy;
  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;
  const disc = b * b - 4 * a * c;
  if (disc < 0) return null;
  const sqrtDisc = Math.sqrt(disc);
  const t = (-b - sqrtDisc) / (2 * a);
  if (t < 0) return null;
  const hx = ox + dx * t - cx;
  const hy = oy + dy * t - cy;
  const len = Math.sqrt(hx * hx + hy * hy);
  return { t, nx: hx / len, ny: hy / len };
}

function rayVsBox(
  ox: number, oy: number, dx: number, dy: number,
  hx: number, hy: number,
): { t: number; nx: number; ny: number } | null {
  const invDx = dx !== 0 ? 1 / dx : (ox < -hx ? -Infinity : Infinity);
  const invDy = dy !== 0 ? 1 / dy : (oy < -hy ? -Infinity : Infinity);

  const tx1 = (-hx - ox) * invDx;
  const tx2 = (hx - ox) * invDx;
  const ty1 = (-hy - oy) * invDy;
  const ty2 = (hy - oy) * invDy;

  const tmin = Math.max(Math.min(tx1, tx2), Math.min(ty1, ty2));
  const tmax = Math.min(Math.max(tx1, tx2), Math.max(ty1, ty2));

  if (tmax < 0 || tmin > tmax) return null;

  const t = tmin >= 0 ? tmin : tmax;
  if (t < 0) return null;

  // Determine hit normal
  const px = ox + dx * t;
  const py = oy + dy * t;
  let nx = 0, ny = 0;
  const eps = 0.001;
  if (Math.abs(px - hx) < eps) nx = 1;
  else if (Math.abs(px + hx) < eps) nx = -1;
  else if (Math.abs(py - hy) < eps) ny = 1;
  else if (Math.abs(py + hy) < eps) ny = -1;

  return { t, nx, ny };
}

function rayVsCapsule(
  ox: number, oy: number, dx: number, dy: number,
  radius: number, height: number, direction: CapsuleDirection,
): { t: number; nx: number; ny: number } | null {
  const hs = (height - radius * 2) * 0.5;

  // Test against both end-cap circles and the rectangle body
  let bestT = Infinity;
  let bestNx = 0, bestNy = 0;

  // End cap centers
  let c1x: number, c1y: number, c2x: number, c2y: number;
  if (direction === CapsuleDirection.Vertical) {
    c1x = 0; c1y = -hs;
    c2x = 0; c2y = hs;
  } else {
    c1x = -hs; c1y = 0;
    c2x = hs; c2y = 0;
  }

  // Test end caps
  for (const [cx, cy] of [[c1x, c1y], [c2x, c2y]] as [number, number][]) {
    const hit = rayVsCircle(ox, oy, dx, dy, cx, cy, radius);
    if (hit && hit.t >= 0 && hit.t < bestT) {
      bestT = hit.t;
      bestNx = hit.nx;
      bestNy = hit.ny;
    }
  }

  // Test rectangle body
  let rectHx: number, rectHy: number;
  if (direction === CapsuleDirection.Vertical) {
    rectHx = radius;
    rectHy = hs;
  } else {
    rectHx = hs;
    rectHy = radius;
  }
  const rectHit = rayVsBox(ox, oy, dx, dy, rectHx, rectHy);
  if (rectHit && rectHit.t >= 0 && rectHit.t < bestT) {
    bestT = rectHit.t;
    bestNx = rectHit.nx;
    bestNy = rectHit.ny;
  }

  return bestT < Infinity ? { t: bestT, nx: bestNx, ny: bestNy } : null;
}

function rayVsPolygon(
  ox: number, oy: number, dx: number, dy: number,
  vertices: PhysVec2[], vertexCount: number,
): { t: number; nx: number; ny: number } | null {
  let tEnter = 0;
  let tExit = Infinity;
  let enterNx = 0, enterNy = 0;

  for (let i = 0; i < vertexCount; i++) {
    const v1 = vertices[i];
    const v2 = vertices[(i + 1) % vertexCount];
    const edgeX = v2.x - v1.x;
    const edgeY = v2.y - v1.y;
    // Outward normal
    const nx = edgeY;
    const ny = -edgeX;
    const len = Math.sqrt(nx * nx + ny * ny);
    if (len < 1e-10) continue;
    const nnx = nx / len;
    const nny = ny / len;

    const denom = dx * nnx + dy * nny;
    const num = (v1.x - ox) * nnx + (v1.y - oy) * nny;

    if (Math.abs(denom) < 1e-10) {
      if (num < 0) return null; // parallel and outside
      continue;
    }

    const t = num / denom;
    if (denom < 0) {
      if (t > tEnter) {
        tEnter = t;
        enterNx = nnx;
        enterNy = nny;
      }
    } else {
      if (t < tExit) tExit = t;
    }

    if (tEnter > tExit) return null;
  }

  if (tEnter < 0) return null;

  return { t: tEnter, nx: enterNx, ny: enterNy };
}

// ============================================================================
// POINT-IN-SHAPE TEST
// ============================================================================

function pointInCollider(
  point: PhysVec2,
  collider: ColliderState,
  body: PhysicsBodyState,
): boolean {
  const cos = Math.cos(body.rotation + collider.rotation);
  const sin = Math.sin(body.rotation + collider.rotation);
  const cx = body.position.x + collider.offset.x * cos - collider.offset.y * sin;
  const cy = body.position.y + collider.offset.x * sin + collider.offset.y * cos;

  const lx = (point.x - cx) * cos + (point.y - cy) * sin;
  const ly = -(point.x - cx) * sin + (point.y - cy) * cos;

  switch (collider.type) {
    case ColliderType.Circle:
      return lx * lx + ly * ly <= collider.radius * collider.radius;
    case ColliderType.Box:
      return Math.abs(lx) <= collider.halfExtents.x && Math.abs(ly) <= collider.halfExtents.y;
    case ColliderType.Polygon: {
      // Winding number test
      let inside = false;
      const verts = collider.vertices;
      const n = collider.vertexCount;
      for (let i = 0, j = n - 1; i < n; j = i++) {
        const vi = verts[i], vj = verts[j];
        if ((vi.y > ly) !== (vj.y > ly) &&
            lx < (vj.x - vi.x) * (ly - vi.y) / (vj.y - vi.y) + vi.x) {
          inside = !inside;
        }
      }
      return inside;
    }
    default:
      return false;
  }
}
